import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material'
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import RequestDialog from '../../dialogs/RequestDialog'
import { useNotify } from 'shared/ui/Notify'
import {
  getSISDistrictInfo,
  resubmitAttempts,
  setSISDistrictInfo,
} from 'providers/mutations'
import {
  IDialogContentType,
  ISISDistrictInfo,
  ISyncLimitsGQL,
} from 'infrastructure/api'
import { useNavigateTo } from 'infrastructure/routing'

function SISDistrictForm({ districtID }: { readonly districtID: number }) {
  const [sisType, setSISType] = useState('')
  const [clientID, setClientID] = useState('')
  const [host, setHost] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [dialogTitle, setDialogTitle] = useState('')
  const [sisUpdate, setSISUpdate] = useState(false)
  const [syncClasses, setSyncClasses] = useState(true)
  const [syncUsers, setSyncUsers] = useState(true)
  const [archiveClasses, setArchiveClasses] = useState(false)
  const [offboardUsers, setOffboardUsers] = useState(false)
  const [automatedOffboarding, setAutomatedOffboarding] = useState(false)
  const [ingestionID, setIngestionID] = useState(-1)
  const [dialogContent, setDialogContent] = useState<IDialogContentType[]>([])
  const [open, setOpen] = useState(false)
  const [archiveClassTooltip, setArchiveClassTooltip] = useState(
    'Select this option to auto-archive classes that are not shared',
  )
  const [offboardUsersTooltip, setOffboardUsersTooltip] = useState(
    'Select this option to auto-offboard users that are not shared',
  )
  const marginBottom = { marginBottom: 5 }
  const oneroster = 'oneroster'

  const notify = useNotify()
  const navigate = useNavigateTo()
  const onClientIDChange = (event: ChangeEvent<HTMLInputElement>) => {
    setClientID(event.target.value)
  }

  const onSISTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSISType(event.target.value)
  }

  const onClientSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
    setClientSecret(event.target.value)
  }
  const onHostChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHost(event.target.value)
  }

  const onSyncUserChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSyncUsers(event.target.checked)
  }
  const onSyncClassesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSyncClasses(event.target.checked)
  }

  const onArchiveClassChange = (event: ChangeEvent<HTMLInputElement>) => {
    setArchiveClasses(event.target.checked)
  }

  const onOffboardUsersChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOffboardUsers(event.target.checked)
  }

  const handleClickOpen = () => {
    const content: IDialogContentType[] = [
      {
        key: 'Integration Type',
        value: sisType,
      },
    ]
    if (sisType === oneroster) {
      content.push(
        { key: 'Host', value: host },
        { key: 'Client ID', value: clientID },
        { key: 'Client Secret', value: clientSecret },
      )
    } else {
      content.push({ key: 'Sync ID', value: clientID })
    }

    if (sisUpdate) {
      setDialogTitle(
        'Are you sure you want to update District Integration Information?',
      )
    } else {
      setDialogTitle(
        'Are you sure you want to add District Integration Information?',
      )
    }

    setDialogContent(content)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    navigate('districts/' + districtID + '/show')
  }

  const displayMessage = (msg: string, msgType: 'error' | 'info') => {
    notify(msg, { type: msgType })
  }

  const isButtonEnabled = useCallback(() => {
    if (sisType === '') {
      return true
    }

    if (sisType === oneroster) {
      if (
        clientID.trim() !== '' &&
        host.trim() !== '' &&
        clientSecret.trim() !== ''
      ) {
        return false
      }
    } else if (clientID.trim() !== '') {
      return false
    }

    return true
  }, [sisType, clientID, host, clientSecret])

  const clientIDLabel = useCallback(() => {
    if (sisType === oneroster) {
      return 'Client ID'
    }
    return 'Sync ID'
  }, [sisType])

  const fetchSISInformation = useCallback(async () => {
    if (districtID) {
      try {
        const sisData = await getSISDistrictInfo(districtID)
        if (sisData !== null || sisData !== undefined) {
          setSISType(sisData.sistype)
          setClientID(sisData.clientid)
          setSISUpdate(true)
          if (sisData.sistype === oneroster) {
            setHost(sisData.host!)
            setClientSecret(sisData.secret!)
          }
          setSyncClasses(sisData.synclimits.syncclass)
          setSyncUsers(sisData.synclimits.syncuser)
          setArchiveClasses(sisData.synclimits.archiveclass)
          setOffboardUsers(sisData.synclimits.offboardusers)
          setAutomatedOffboarding(sisData.synclimits.automatedoffboarding)
          setIngestionID(sisData.ingestionid!)
          if (automatedOffboarding) {
            setArchiveClassTooltip(
              'Currently locked due to Automated offboarding being enabled',
            )
            setOffboardUsersTooltip(
              'Currently locked due to Automated offboarding being enabled',
            )
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Error getting District SIS data', err)
      }
    }
  }, [districtID])

  useEffect(() => {
    fetchSISInformation()
  }, [fetchSISInformation])

  const resyncSubmits = async () => {
    const response = await resubmitAttempts(districtID)
    if (response.errors) {
      displayMessage(response.errors[0].message, 'error')
    } else {
      displayMessage(String(response.data), 'info')
    }
  }

  const submitSISData = async () => {
    const synclimits: ISyncLimitsGQL = {
      syncclass: syncClasses,
      syncuser: syncUsers,
      archiveclass: archiveClasses,
      offboardusers: offboardUsers,
      automatedoffboarding: automatedOffboarding,
    }
    const sisDistrictData: ISISDistrictInfo = {
      districtid: districtID,
      sistype: sisType,
      clientid: clientID.trim(),
      synclimits: synclimits,
    }
    if (sisUpdate) {
      sisDistrictData.ingestionid = ingestionID
    }
    if (sisType === oneroster) {
      sisDistrictData.host = host.trim()
      sisDistrictData.secret = clientSecret.trim()
    }
    const resp = await setSISDistrictInfo(sisDistrictData)
    if (resp.errors) {
      displayMessage(resp.errors[0].message, 'error')
    } else {
      displayMessage('District Setup Started', 'info')
    }
  }

  return (
    <div>
      <Box sx={{ width: 300, display: 'inline' }} style={{ float: 'left' }}>
        <FormControl fullWidth>
          <TextField
            value={sisType}
            label="Select Integration Type"
            variant="outlined"
            onChange={onSISTypeChange}
            select
            size="small"
          >
            <MenuItem value="oneroster">OneRoster</MenuItem>
            <MenuItem value="clever">Clever</MenuItem>
            <MenuItem value="classlink">ClassLink</MenuItem>
            <MenuItem value="edlink">Edlink</MenuItem>
            <MenuItem value="manual">Manual/CSV</MenuItem>
          </TextField>
        </FormControl>
        <br />
        <br />
        {sisType === 'oneroster' && (
          <>
            <FormControl fullWidth className="edit-sis-form-box">
              <TextField
                value={clientSecret}
                label="Client Secret"
                variant="outlined"
                onChange={onClientSecretChange}
                type="password"
                size="small"
                style={marginBottom}
                inputProps={{
                  'aria-label': 'Client Secret',
                }}
              />
            </FormControl>
            <FormControl fullWidth className="edit-sis-form-box">
              <TextField
                value={host}
                label="Host"
                variant="outlined"
                onChange={onHostChange}
                size="small"
                style={marginBottom}
                inputProps={{
                  'aria-label': 'Host',
                }}
              />
            </FormControl>
          </>
        )}
        <FormControl fullWidth className="edit-sis-form-box">
          <TextField
            value={clientID}
            label={clientIDLabel()}
            variant="outlined"
            onChange={onClientIDChange}
            size="small"
          />
        </FormControl>
        <br />
        <br />
        <FormControl fullWidth className="edit-sis-form-box">
          <Button
            onClick={handleClickOpen}
            variant="outlined"
            color="secondary"
            disabled={isButtonEnabled()}
          >
            {sisUpdate ? 'Update' : 'Submit'}
          </Button>
        </FormControl>
      </Box>
      <RequestDialog
        open={open}
        title={dialogTitle}
        content={dialogContent}
        submitRequest={submitSISData}
        waitingTime={3000}
        onClose={handleClose}
      />
      {sisType !== '' && (
        <>
          <Box
            sx={{ width: 300, border: 1, display: 'inline' }}
            style={{ float: 'left', margin: '3px' }}
          >
            <h3 style={{ textAlign: 'center', width: '100%' }}>
              Sync Settings
            </h3>
            <FormControl
              fullWidth
              className="edit-sis-form-box"
              style={{
                alignContent: 'center',
                alignItems: 'left',
                marginLeft: '10px',
              }}
            >
              <Tooltip
                title="Select this option to sync users"
                placement="right-start"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncUsers}
                      onChange={onSyncUserChange}
                      name="syncUsers"
                    />
                  }
                  label="Sync Users"
                />
              </Tooltip>
              <Tooltip
                title="Select this option to sync classes"
                placement="right-start"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncClasses}
                      onChange={onSyncClassesChange}
                      name="syncClasses"
                    />
                  }
                  label="Sync Classes"
                />
              </Tooltip>
              <Tooltip title={archiveClassTooltip} placement="right-start">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={archiveClasses}
                      onChange={onArchiveClassChange}
                      name="archiveClasses"
                      disabled={automatedOffboarding}
                    />
                  }
                  label="Auto-Archive Classes"
                />
              </Tooltip>
              <Tooltip title={offboardUsersTooltip} placement="right-start">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={offboardUsers}
                      onChange={onOffboardUsersChange}
                      name="offboardUsers"
                      disabled={automatedOffboarding}
                    />
                  }
                  label="Auto-Offboard Users"
                />
              </Tooltip>
            </FormControl>
          </Box>
        </>
      )}
      {sisType === 'edlink' && sisUpdate && (
        <>
          <FormControl fullWidth className="edit-sis-form-box">
            <Button
              onClick={resyncSubmits}
              variant="outlined"
              color="secondary"
            >
              Resync Attempts
            </Button>
          </FormControl>
        </>
      )}
    </div>
  )
}
export default SISDistrictForm
